import { useState } from "react";
import { ApiError, type PollDraft } from "@shared/api";
import { Dialog, Field } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { Switch } from "@shared/ui/switch";

/** Сроки — как в Discord: от часа до недели. */
const DURATIONS: { hours: number; label: string }[] = [
  { hours: 1, label: "1 час" },
  { hours: 4, label: "4 часа" },
  { hours: 8, label: "8 часов" },
  { hours: 24, label: "24 часа" },
  { hours: 72, label: "3 дня" },
  { hours: 168, label: "1 неделя" },
];

const QUESTION_MAX = 300;
const ANSWER_MAX = 55;
const ANSWERS_MAX = 10;

/**
 * Окно «Создать опрос» — как в Discord: вопрос, от двух до десяти ответов, срок
 * и «можно несколько». Опрос уходит отдельным сообщением.
 */
export function PollDialog({ onSend, onClose }: { onSend: (poll: PollDraft) => Promise<void>; onClose: () => void }) {
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState(["", ""]);
  const [hours, setHours] = useState(24);
  const [multiselect, setMultiselect] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const filled = answers.map((answer) => answer.trim()).filter(Boolean);
  const ready = question.trim().length > 0 && filled.length >= 2;

  async function send() {
    setBusy(true);
    setProblem(null);
    try {
      await onSend({ question: question.trim(), answers: filled, multiselect, hours });
      onClose();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Опрос не ушёл");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Создать опрос"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={!ready || busy} onClick={() => void send()}>
            Опубликовать
          </Button>
        </>
      }
    >
      <div className="poll-form">
        <Field label="Вопрос">
          <Input
            value={question}
            maxLength={QUESTION_MAX}
            placeholder="О чём спросить?"
            onChange={(event) => setQuestion(event.target.value)}
          />
        </Field>

        <Field label="Ответы">
          <div className="poll-form-answers">
            {answers.map((answer, index) => (
              <div key={index} className="line" style={{ gap: 6 }}>
                <Input
                  className="grow"
                  value={answer}
                  maxLength={ANSWER_MAX}
                  placeholder={`Ответ ${index + 1}`}
                  aria-label={`Ответ ${index + 1}`}
                  onChange={(event) =>
                    setAnswers((previous) => previous.map((item, at) => (at === index ? event.target.value : item)))
                  }
                />
                {answers.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Убрать ответ ${index + 1}`}
                    onClick={() => setAnswers((previous) => previous.filter((_, at) => at !== index))}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
            {answers.length < ANSWERS_MAX && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                style={{ alignSelf: "flex-start" }}
                onClick={() => setAnswers((previous) => [...previous, ""])}
              >
                Добавить ответ
              </Button>
            )}
          </div>
        </Field>

        <div className="line" style={{ gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <Field label="Срок">
            <Select value={String(hours)} onValueChange={(value) => setHours(Number(value))}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((item) => (
                  <SelectItem key={item.hours} value={String(item.hours)}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <label className="line" style={{ gap: 8, alignItems: "center" }}>
            <Switch checked={multiselect} onCheckedChange={setMultiselect} aria-label="Можно несколько ответов" />
            <span className="small">Можно несколько ответов</span>
          </label>
        </div>

        {problem && <div className="small danger-text">{problem}</div>}
      </div>
    </Dialog>
  );
}
